import { getNodeLabel } from "@/common";
import FocusNode from "@/components/FocusNode/FocusNode";
import AttributeContext from "@/pages/models/release/AttributeContext";
import ReleaseContextProvider from "@/pages/models/release/ReleaseContextProvider";
import { useDialogModal } from "@/ui/dialogs/useDialogModal";
import styled from "@emotion/styled";
import clsx from "clsx";
import { last } from "lodash";
import React, { useState, useRef } from "react";

import { statusColor } from "@/features/tasks/taskUtils";
import { useParsedGraph } from "@/pages/models/release/GraphContext";
import { useSessionDecisionGraph } from "@/pages/sessions/sessionGraph";
import { graphNodeByKey } from "@/components/GraphVisualisation/decisionGraphView";
import { DateTime, StatusCircle } from "@/pages/sessions/components";
import AttributeValue from "@imminently/decisively-ui/AttributeValue";
import Button from "@imminently/decisively-ui/Button";
import Icon from "@imminently/decisively-ui/Icon";
import { Popover, Tooltip, Typography } from "@mui/material";
import { TaskStatusEnum } from "@packages/commons";

import { useFullRelease } from "@/common/hooks_useFullRelease";
import type { PhaseTask } from "./types";

const CompareIcon = () => (
  <svg
    aria-hidden="true"
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M8.00002 1C7.86741 1 7.74024 1.05268 7.64647 1.14645C7.5527 1.24021 7.50002 1.36739 7.50002 1.5C7.50002 1.63261 7.5527 1.75979 7.64647 1.85355C7.74024 1.94732 7.86741 2 8.00002 2C8.13263 2 8.2598 1.94732 8.35357 1.85355C8.44734 1.75979 8.50002 1.63261 8.50002 1.5C8.50002 1.36739 8.44734 1.24021 8.35357 1.14645C8.2598 1.05268 8.13263 1 8.00002 1ZM4.16669 1.33333C3.34419 1.33333 2.66669 2.01083 2.66669 2.83333V13.1667C2.66669 13.9892 3.34419 14.6667 4.16669 14.6667H6.66669V13.6667H4.16669C3.88452 13.6667 3.66669 13.4488 3.66669 13.1667V2.83333C3.66669 2.55117 3.88452 2.33333 4.16669 2.33333H6.66669V1.33333H4.16669ZM9.33335 1.33333V2.33333H11.8334C12.1155 2.33333 12.3334 2.55117 12.3334 2.83333V13.1667C12.3334 13.4488 12.1155 13.6667 11.8334 13.6667H9.33335V14.6667H11.8334C12.6559 14.6667 13.3334 13.9892 13.3334 13.1667V2.83333C13.3334 2.01083 12.6559 1.33333 11.8334 1.33333H9.33335ZM8.00002 2.66667C7.86741 2.66667 7.74024 2.71935 7.64647 2.81311C7.5527 2.90688 7.50002 3.03406 7.50002 3.16667C7.50002 3.29927 7.5527 3.42645 7.64647 3.52022C7.74024 3.61399 7.86741 3.66667 8.00002 3.66667C8.13263 3.66667 8.2598 3.61399 8.35357 3.52022C8.44734 3.42645 8.50002 3.29927 8.50002 3.16667C8.50002 3.03406 8.44734 2.90688 8.35357 2.81311C8.2598 2.71935 8.13263 2.66667 8.00002 2.66667ZM5.16669 4.33333C4.89035 4.33333 4.66669 4.557 4.66669 4.83333C4.66669 5.10967 4.89035 5.33333 5.16669 5.33333H6.66669V4.33333H5.16669ZM8.00002 4.33333C7.86741 4.33333 7.74024 4.38601 7.64647 4.47978C7.5527 4.57355 7.50002 4.70073 7.50002 4.83333C7.50002 4.96594 7.5527 5.09312 7.64647 5.18689C7.74024 5.28065 7.86741 5.33333 8.00002 5.33333C8.13263 5.33333 8.2598 5.28065 8.35357 5.18689C8.44734 5.09312 8.50002 4.96594 8.50002 4.83333C8.50002 4.70073 8.44734 4.57355 8.35357 4.47978C8.2598 4.38601 8.13263 4.33333 8.00002 4.33333ZM9.33335 4.33333V5.33333H10.8334C11.1097 5.33333 11.3334 5.10967 11.3334 4.83333C11.3334 4.557 11.1097 4.33333 10.8334 4.33333H9.33335ZM8.00002 6C7.86741 6 7.74024 6.05268 7.64647 6.14645C7.5527 6.24022 7.50002 6.36739 7.50002 6.5C7.50002 6.63261 7.5527 6.75979 7.64647 6.85355C7.74024 6.94732 7.86741 7 8.00002 7C8.13263 7 8.2598 6.94732 8.35357 6.85355C8.44734 6.75979 8.50002 6.63261 8.50002 6.5C8.50002 6.36739 8.44734 6.24022 8.35357 6.14645C8.2598 6.05268 8.13263 6 8.00002 6ZM5.16669 6.33333C4.89035 6.33333 4.66669 6.557 4.66669 6.83333C4.66669 7.10967 4.89035 7.33333 5.16669 7.33333H6.66669V6.33333H5.16669ZM9.33335 6.33333V7.33333H10.8334C11.1097 7.33333 11.3334 7.10967 11.3334 6.83333C11.3334 6.557 11.1097 6.33333 10.8334 6.33333H9.33335ZM8.00002 7.66667C7.86741 7.66667 7.74024 7.71935 7.64647 7.81311C7.5527 7.90688 7.50002 8.03406 7.50002 8.16667C7.50002 8.29927 7.5527 8.42645 7.64647 8.52022C7.74024 8.61399 7.86741 8.66667 8.00002 8.66667C8.13263 8.66667 8.2598 8.61399 8.35357 8.52022C8.44734 8.42645 8.50002 8.29927 8.50002 8.16667C8.50002 8.03406 8.44734 7.90688 8.35357 7.81311C8.2598 7.71935 8.13263 7.66667 8.00002 7.66667ZM9.33335 8.33333V9.33333H10.8334C11.1097 9.33333 11.3334 9.10967 11.3334 8.83333C11.3334 8.557 11.1097 8.33333 10.8334 8.33333H9.33335ZM8.00002 9.33333C7.86741 9.33333 7.74024 9.38601 7.64647 9.47978C7.5527 9.57355 7.50002 9.70073 7.50002 9.83333C7.50002 9.96594 7.5527 10.0931 7.64647 10.1869C7.74024 10.2807 7.86741 10.3333 8.00002 10.3333C8.13263 10.3333 8.2598 10.2807 8.35357 10.1869C8.44734 10.0931 8.50002 9.96594 8.50002 9.83333C8.50002 9.70073 8.44734 9.57355 8.35357 9.47978C8.2598 9.38601 8.13263 9.33333 8.00002 9.33333ZM9.33335 10.3333V11.3333H10.8334C11.1097 11.3333 11.3334 11.1097 11.3334 10.8333C11.3334 10.557 11.1097 10.3333 10.8334 10.3333H9.33335ZM8.00002 10.8333C7.86741 10.8333 7.74024 10.886 7.64647 10.9798C7.5527 11.0735 7.50002 11.2007 7.50002 11.3333C7.50002 11.4659 7.5527 11.5931 7.64647 11.6869C7.74024 11.7807 7.86741 11.8333 8.00002 11.8333C8.13263 11.8333 8.2598 11.7807 8.35357 11.6869C8.44734 11.5931 8.50002 11.4659 8.50002 11.3333C8.50002 11.2007 8.44734 11.0735 8.35357 10.9798C8.2598 10.886 8.13263 10.8333 8.00002 10.8333ZM8.00002 12.5C7.86741 12.5 7.74024 12.5527 7.64647 12.6464C7.5527 12.7402 7.50002 12.8674 7.50002 13C7.50002 13.1326 7.5527 13.2598 7.64647 13.3536C7.74024 13.4473 7.86741 13.5 8.00002 13.5C8.13263 13.5 8.2598 13.4473 8.35357 13.3536C8.44734 13.2598 8.50002 13.1326 8.50002 13C8.50002 12.8674 8.44734 12.7402 8.35357 12.6464C8.2598 12.5527 8.13263 12.5 8.00002 12.5ZM8.00002 14.1667C7.86741 14.1667 7.74024 14.2193 7.64647 14.3131C7.5527 14.4069 7.50002 14.5341 7.50002 14.6667C7.50002 14.7993 7.5527 14.9265 7.64647 15.0202C7.74024 15.114 7.86741 15.1667 8.00002 15.1667C8.13263 15.1667 8.2598 15.114 8.35357 15.0202C8.44734 14.9265 8.50002 14.7993 8.50002 14.6667C8.50002 14.5341 8.44734 14.4069 8.35357 14.3131C8.2598 14.2193 8.13263 14.1667 8.00002 14.1667Z"
      fill="#0A0A0A"
    />
  </svg>
);
const BalanceIcon = () => (
  <svg
    aria-hidden="true"
    width="14"
    height="13"
    viewBox="0 0 14 13"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M0.666656 12.6667V11.6667H6.49999V4.25525C6.19832 4.17658 5.93443 4.02742 5.70832 3.80775C5.48221 3.58808 5.32771 3.31886 5.24482 3.00008H3.03849L4.99999 7.73725C4.98721 8.23725 4.7701 8.66247 4.34866 9.01291C3.92732 9.36336 3.42221 9.53858 2.83332 9.53858C2.24443 9.53858 1.73932 9.36336 1.31799 9.01291C0.896657 8.66247 0.679545 8.23725 0.666656 7.73725L2.62816 3.00008H1.33332V2.00008H5.24482C5.34827 1.61975 5.56043 1.30247 5.88132 1.04825C6.20232 0.793915 6.57521 0.666748 6.99999 0.666748C7.42477 0.666748 7.79766 0.793915 8.11866 1.04825C8.43955 1.30247 8.65171 1.61975 8.75516 2.00008H12.6667V3.00008H11.3718L13.3333 7.73725C13.3204 8.23725 13.1033 8.66247 12.682 9.01291C12.2607 9.36336 11.7555 9.53858 11.1667 9.53858C10.5778 9.53858 10.0727 9.36336 9.65132 9.01291C9.22988 8.66247 9.01277 8.23725 8.99999 7.73725L10.9615 3.00008H8.75516C8.67227 3.31886 8.51777 3.58808 8.29166 3.80775C8.06555 4.02742 7.80166 4.17658 7.49999 4.25525V11.6667H13.3333V12.6667H0.666656ZM9.87182 7.70525H12.4615L11.1667 4.58091L9.87182 7.70525ZM1.53849 7.70525H4.12816L2.83332 4.58091L1.53849 7.70525ZM6.99999 3.33341C7.23166 3.33341 7.42843 3.25241 7.59032 3.09041C7.75232 2.92853 7.83332 2.73175 7.83332 2.50008C7.83332 2.26841 7.75232 2.07164 7.59032 1.90975C7.42843 1.74775 7.23166 1.66675 6.99999 1.66675C6.76832 1.66675 6.57155 1.74775 6.40966 1.90975C6.24766 2.07164 6.16666 2.26841 6.16666 2.50008C6.16666 2.73175 6.24766 2.92853 6.40966 3.09041C6.57155 3.25241 6.76832 3.33341 6.99999 3.33341Z"
      fill="#1C1B1F"
    />
  </svg>
);
const TasksIcon = () => (
  <svg
    aria-hidden="true"
    width="12"
    height="14"
    viewBox="0 0 12 14"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M4.83329 0.333252C4.06776 0.333252 3.43519 0.922126 3.35022 1.66659H2.16663C1.34413 1.66659 0.666626 2.34408 0.666626 3.16659V12.1666C0.666626 12.9891 1.34413 13.6666 2.16663 13.6666H9.83329C10.6558 13.6666 11.3333 12.9891 11.3333 12.1666V3.16659C11.3333 2.34408 10.6558 1.66659 9.83329 1.66659H8.6497C8.56472 0.922126 7.93216 0.333252 7.16663 0.333252H4.83329ZM4.83329 1.33325H7.16663C7.44879 1.33325 7.66663 1.55109 7.66663 1.83325C7.66663 2.11542 7.44879 2.33325 7.16663 2.33325H4.83329C4.55113 2.33325 4.33329 2.11542 4.33329 1.83325C4.33329 1.55109 4.55113 1.33325 4.83329 1.33325ZM2.16663 2.66659H3.5898C3.86037 3.06722 4.3182 3.33325 4.83329 3.33325H7.16663C7.68172 3.33325 8.13955 3.06722 8.41012 2.66659H9.83329C10.1155 2.66659 10.3333 2.88442 10.3333 3.16659V12.1666C10.3333 12.4488 10.1155 12.6666 9.83329 12.6666H2.16663C1.88446 12.6666 1.66663 12.4488 1.66663 12.1666V3.16659C1.66663 2.88442 1.88446 2.66659 2.16663 2.66659Z"
      fill="#0A0A0A"
    />
    <path
      fill-rule="evenodd"
      clip-rule="evenodd"
      d="M7 4.66659L3.33333 8.33325C3.33333 8.33325 3 8.99992 2.66667 9.99992C2.33333 10.9999 2.66667 11.3333 3.66667 10.9999C4.66667 10.6666 5.33333 10.3333 5.33333 10.3333L9 6.66659C9 6.66659 9.33333 6.291 9.33333 5.99992C9.33333 5.70884 9 5.33325 9 5.33325L8.33333 4.66659C8.33333 4.66659 8 4.33325 7.66667 4.33325C7.33333 4.33325 7 4.66659 7 4.66659ZM3.66667 9.99992L4 8.99992L7.66667 5.33325L8.33333 5.99992L4.66667 9.66659L3.66667 9.99992Z"
      fill="#0A0A0A"
    />
  </svg>
);
const ApiIcon = () => (
  <svg
    aria-hidden="true"
    width="14"
    height="14"
    viewBox="0 0 14 14"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M7.00006 8.33364L5.66672 7.0003L7.00006 5.66697L8.33339 7.0003L7.00006 8.33364ZM5.58339 4.5323L4.18606 3.13497L7.00006 0.320801L9.81406 3.13497L8.41672 4.5323L7.00006 3.11563L5.58339 4.5323ZM3.13472 9.8143L0.320557 7.0003L3.13472 4.1863L4.53206 5.58363L3.11539 7.0003L4.53206 8.41697L3.13472 9.8143ZM10.8654 9.8143L9.46806 8.41697L10.8847 7.0003L9.46806 5.58363L10.8654 4.1863L13.6796 7.0003L10.8654 9.8143ZM7.00006 13.6798L4.18606 10.8656L5.58339 9.4683L7.00006 10.885L8.41672 9.4683L9.81406 10.8656L7.00006 13.6798Z"
      fill="#1C1B1F"
    />
  </svg>
);
const InterviewIcon = () => (
  <svg
    aria-hidden="true"
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M3.49998 1.66675C2.30933 1.66675 1.33331 2.64277 1.33331 3.83341V9.16675V11.1505C1.33331 11.8127 2.14401 12.225 2.67967 11.836L4.03123 10.8529C4.20052 11.8801 5.09234 12.6667 6.16665 12.6667H10.3372L13.3203 14.836C13.4686 14.9437 13.6427 14.9988 13.8183 14.9988C13.9497 14.9988 14.0818 14.968 14.2038 14.9057C14.4894 14.7603 14.6666 14.4708 14.6666 14.1505V6.83342C14.6666 5.63875 13.6946 4.66675 12.5 4.66675H12V3.83341C12 2.64277 11.024 1.66675 9.83331 1.66675H3.49998ZM3.49998 2.66675H9.83331C10.4833 2.66675 11 3.18339 11 3.83341V7.50008C11 8.1501 10.4833 8.66675 9.83331 8.66675H5.49998C5.39424 8.66673 5.29121 8.70024 5.20571 8.76245L2.33331 10.8516V9.16675V3.83341C2.33331 3.18339 2.84996 2.66675 3.49998 2.66675ZM4.49998 4.00008C4.43372 3.99914 4.36794 4.01138 4.30646 4.03609C4.24497 4.0608 4.18901 4.09748 4.14183 4.144C4.09464 4.19052 4.05718 4.24596 4.0316 4.30709C4.00603 4.36822 3.99286 4.43382 3.99286 4.50008C3.99286 4.56634 4.00603 4.63195 4.0316 4.69307C4.05718 4.7542 4.09464 4.80964 4.14183 4.85616C4.18901 4.90268 4.24497 4.93936 4.30646 4.96407C4.36794 4.98878 4.43372 5.00102 4.49998 5.00008H8.83331C8.89957 5.00102 8.96535 4.98878 9.02683 4.96407C9.08832 4.93936 9.14428 4.90268 9.19146 4.85616C9.23865 4.80964 9.27612 4.7542 9.30169 4.69307C9.32727 4.63195 9.34044 4.56634 9.34044 4.50008C9.34044 4.43382 9.32727 4.36822 9.30169 4.30709C9.27612 4.24596 9.23865 4.19052 9.19146 4.144C9.14428 4.09748 9.08832 4.0608 9.02683 4.03609C8.96535 4.01138 8.89957 3.99914 8.83331 4.00008H4.49998ZM12 5.66675H12.5C13.1433 5.66675 13.6666 6.19008 13.6666 6.83342V13.8516L10.7943 11.7625C10.7089 11.7005 10.6056 11.6667 10.5 11.6667H6.16665C5.52331 11.6667 4.99998 11.1434 4.99998 10.5001V10.1485L5.66274 9.66675H9.83331C11.024 9.66675 12 8.69073 12 7.50008V5.66675ZM4.49998 6.33342C4.43372 6.33248 4.36794 6.34472 4.30646 6.36943C4.24497 6.39413 4.18901 6.43081 4.14183 6.47733C4.09464 6.52386 4.05718 6.57929 4.0316 6.64042C4.00603 6.70155 3.99286 6.76715 3.99286 6.83342C3.99286 6.89968 4.00603 6.96528 4.0316 7.02641C4.05718 7.08754 4.09464 7.14297 4.14183 7.18949C4.18901 7.23602 4.24497 7.2727 4.30646 7.2974C4.36794 7.32211 4.43372 7.33435 4.49998 7.33342H7.83331C7.89957 7.33435 7.96535 7.32211 8.02683 7.2974C8.08832 7.2727 8.14428 7.23602 8.19146 7.18949C8.23865 7.14297 8.27612 7.08754 8.30169 7.02641C8.32727 6.96528 8.34043 6.89968 8.34043 6.83342C8.34043 6.76715 8.32727 6.70155 8.30169 6.64042C8.27612 6.57929 8.23865 6.52386 8.19146 6.47733C8.14428 6.43081 8.08832 6.39413 8.02683 6.36943C7.96535 6.34472 7.89957 6.33248 7.83331 6.33342H4.49998Z"
      fill="#0A0A0A"
    />
  </svg>
);

const CommonIconWrap = styled.div`
  width: 1.5rem;
  height: 1.5rem;
  background-color: #F9F9F9;
  border: 1px solid #E5E5E5;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const CommonIconBtn = styled(Button)`
  box-sizing: border-box;
  width: 1.5rem;
  height: 1.5rem;
  background-color: #F9F9F9;
  border: 1px solid #E5E5E5;
  display: flex;
  align-items: center;
  justify-content: center;

  svg {
    width: 1rem;
    height: 1rem;
  }
`;

const HelpIconButton = styled(Button)`
  position: absolute;
  top: -8px;
  right: -8px;
  padding: 4px;
  background-color: white;
  border: 1px solid #E5E5E5;
  width: 20px;
  height: 20px;

  &:hover {
    background-color: #F5F5F5;
  }

  .MuiSvgIcon-root {
    font-size: 12px;
    color: #767676;
  }
`;

const Wrap = styled.div`
  position: relative;
  border: 1px solid #E5E5E5;
  padding: 0.75rem 0.75rem 0.75rem 1.5rem;
  border-radius: 0.5rem;
  margin-left: 0.75rem;
  background-color: #F9F9F980;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  opacity: 0.5;

  &.relevant { opacity: 1; }
`;

const AbsIconWrap = styled(CommonIconWrap)`
  position: absolute;
  top: 0.75rem;
  left: -0.75rem;
`;

const TopRow = styled.div`
  display: flex;
  gap: 1rem;
  align-items: center;
`;

const TitleAndIcon = styled.div`
  display: flex;
  gap: 0.5rem;
  align-items: center;
  flex-grow: 1;
`;

const Title = styled(Typography)`
  font-size: 0.875rem;
  font-weight: 600;
  line-height: 1.2;
`;

const Divider = styled.div`
  margin: 0 -1rem 0 -1.5rem;
  height: 1px;
  background-color: #F3F3F3;
`;

const BottomWrap = styled.div`
  display: flex;
  gap: 1rem;
  align-items: center;
`;

const BottomTexts = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  flex-grow: 1;
`;

const PersonText = styled(Typography)`
  font-size: 0.75rem;
  font-weight: 600;
  line-height: 1.2;
  color: #767676;
`;

export type PhaseTaskCardProps = {
  className?: string;
  task: PhaseTask;
};

const getTaskIcon = (task: PhaseTask): React.ReactNode => {
  switch (task.type) {
    case "api":
      return <ApiIcon />;
    case "task":
      return <TasksIcon />;
    case "interview":
      return <InterviewIcon />;
    default:
      return <Icon name="warning" />;
  }
};

export const PhaseTaskCard: React.FC<PhaseTaskCardProps> = (p) => {
  const { className, task } = p;
  const { openDialogModal } = useDialogModal();
  // in a session, its decision graph (values); elsewhere the release's rule graph
  const releaseGraph = useParsedGraph();
  const graph: any = useSessionDecisionGraph() ?? releaseGraph;
  const release = useFullRelease();
  const outcomeNode = graphNodeByKey(graph, task.outcomeAttribute);

  const color = statusColor[task.status ?? TaskStatusEnum.NotStarted];
  const icon = getTaskIcon(task);
  const finalClassName = clsx([className, task.relevant && "relevant"]);
  const isComplete = task.status === TaskStatusEnum.Approved;
  const displayUserInfo = task.assignedTo !== undefined && task.completedAt !== undefined;
  const personTextPrefix = isComplete ? "Completed by" : "Assigned to";
  const dateTextPrefix = isComplete ? "Date" : "Due";

  const handleHelpClick = React.useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      const node = graphNodeByKey(graph, task.attribute);
      console.log("NODE", node, graph, task);

      if (!node || !release) return;

      openDialogModal(
        {
          title: getNodeLabel(node),
          maxWidth: "xl",
          allowResize: true,
        },
        <ReleaseContextProvider release={release}>
          <AttributeContext.Provider value={node}>
            <FocusNode
              node={node}
              graph={graph}
              onNodeChange={() => {}}
            />
          </AttributeContext.Provider>
        </ReleaseContextProvider>,
      );
    },
    [graph, openDialogModal, release],
  );

  return (
    <Tooltip title={task.relevant ? "" : "Not relevant"}>
      <Wrap className={finalClassName}>
        <HelpIconButton
          onClick={handleHelpClick}
          label="Help"
        >
          <Icon name="help" />
        </HelpIconButton>

        <AbsIconWrap>{icon}</AbsIconWrap>

        <TopRow>
          <TitleAndIcon>
            <StatusCircle color={color} />

            <Title>{task.title}</Title>
          </TitleAndIcon>

          <AttributeValue
            hideTemporal
            node={outcomeNode}
          />
        </TopRow>
        <>
          <Divider />
          <BottomWrap>
            <BottomTexts>
              <PersonText>
                {personTextPrefix}: {task.assignedTo || "Unassigned"}
              </PersonText>
              <DateTime
                hideIcon
                value={task.completedAt}
                prefix={dateTextPrefix}
              />
            </BottomTexts>
          </BottomWrap>
        </>
      </Wrap>
    </Tooltip>
  );
};
